import { createContext, useCallback, useContext, useState } from 'react';
import BulkSignRevisionModal from 'components/modals/bulk-sign-revision-modal/BulkSignRevisionModal';
import { nanoid } from 'nanoid';

/**
 * Parameters of the BulkSignRevisionModal function.
 */
export interface IBulkSignRevisionModalParams {
  revisionIds: number[];
}

/**
 * BulkSignRevisionModal function.
 */
export type IBulkSignRevisionModal = (params: IBulkSignRevisionModalParams) => void;

/**
 * Typedef for the BulkSignRevisionModalProvider context.
 */
export interface IBulkSignRevisionModalContext {
  bulkSignRevisionModal: IBulkSignRevisionModal;
}

/**
 * The bulkSignRevisionModal context.
 */
const BulkSignRevisionModalContext = createContext<IBulkSignRevisionModalContext>(undefined!);

/**
 * Provides the BulkSignRevisionModalContext.
 */
export default function BulkSignRevisionModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, setOpened] = useState(false);
  const [key, setKey] = useState(nanoid);

  const [params, setParams] = useState<IBulkSignRevisionModalParams>({
    revisionIds: [],
  });

  const bulkSignRevisionModal: IBulkSignRevisionModal = useCallback((params) => {
    setParams(params);
    setOpened(true);
    setKey(nanoid());
  }, []);

  return (
    <BulkSignRevisionModalContext.Provider value={{ bulkSignRevisionModal }}>
      {children}
      <BulkSignRevisionModal {...params} key={key} opened={opened} onClose={() => setOpened(false)} />
    </BulkSignRevisionModalContext.Provider>
  );
}

/**
 * Uses the BulkSignRevisionModalContext.
 */
export function useBulkSignRevisionModal() {
  return useContext(BulkSignRevisionModalContext);
}
